import {
  FOC_INCIDENTS_TOUR_STEPS,
  FOC_INCIDENTS_TOUR_WIZARD_HREF,
  type FocIncidentsTourStepId,
  type FocIncidentsTourSurface,
} from './focIncidentsTourSteps';

/**
 * The tour runs outside React, so Hub Incidents and Create Incident hosts
 * register the UI moves they alone can perform.
 */
export type FocIncidentsTourApi = {
  /** Optional; falls back to window.location when omitted. */
  getLocation?: () => { pathname: string; search: string };
  showIncidents: () => void;
  openMoreMenu: () => void;
  closeMoreMenu: () => void;
  openCreateMenu: () => void;
  closeCreateMenu: () => void;
  openCreateIncident: () => void;
  expandFirstIncident: () => void;
  collapseFirstIncident: () => void;
};

let api: Partial<FocIncidentsTourApi> = {};
let pendingStep: FocIncidentsTourStepId | null = null;

export function registerFocIncidentsTourApi(
  partial: Partial<FocIncidentsTourApi>
): () => void {
  api = { ...api, ...partial };
  if (pendingStep) {
    const step = pendingStep;
    const needed = surfaceFor(step);
    if (currentSurface() === needed) {
      pendingStep = null;
      prepareFocIncidentsStep(step);
    }
  }
  return () => {
    for (const key of Object.keys(partial) as (keyof FocIncidentsTourApi)[]) {
      if (api[key] === partial[key]) {
        delete api[key];
      }
    }
  };
}

export function getFocIncidentsTourApi(): Partial<FocIncidentsTourApi> {
  return api;
}

function surfaceFor(id: FocIncidentsTourStepId): FocIncidentsTourSurface {
  return (
    FOC_INCIDENTS_TOUR_STEPS.find((step) => step.id === id)?.surface ??
    'incidents'
  );
}

function readLocation(): { pathname: string; search: string } {
  const fromHost = getFocIncidentsTourApi().getLocation?.();
  if (fromHost) return fromHost;
  if (typeof window !== 'undefined' && window.location) {
    return {
      pathname: window.location.pathname,
      search: window.location.search,
    };
  }
  return { pathname: '/hub', search: '?openTab=Incidents' };
}

function currentSurface(): FocIncidentsTourSurface {
  const { pathname } = readLocation();
  if (pathname.includes('/create/incident')) return 'createWizard';
  return 'incidents';
}

function ensureSurface(id: FocIncidentsTourStepId): boolean {
  const needed = surfaceFor(id);
  const current = currentSurface();
  const host = getFocIncidentsTourApi();

  if (needed === 'createWizard') {
    if (current === 'createWizard') {
      pendingStep = null;
      return true;
    }
    pendingStep = id;
    host.openCreateIncident?.();
    return false;
  }

  // `needed` is already narrowed away from createWizard above.
  if (current === 'createWizard') {
    pendingStep = id;
    host.showIncidents?.();
    return false;
  }

  pendingStep = null;
  return true;
}

export function prepareFocIncidentsStep(id: FocIncidentsTourStepId) {
  if (!ensureSurface(id)) return;

  const host = getFocIncidentsTourApi();
  const surface = surfaceFor(id);

  if (surface === 'createWizard') {
    host.closeCreateMenu?.();
    return;
  }

  if (surface === 'createMenu') {
    host.showIncidents?.();
    host.openCreateMenu?.();
    return;
  }

  // incidents list
  host.closeCreateMenu?.();
  host.showIncidents?.();

  switch (id) {
    case 'usi001':
      host.openMoreMenu?.();
      host.collapseFirstIncident?.();
      return;
    case 'usi005':
    case 'usi006':
      host.closeMoreMenu?.();
      host.expandFirstIncident?.();
      return;
    case 'usi007':
      host.closeMoreMenu?.();
      host.collapseFirstIncident?.();
      return;
    case 'usi003':
    case 'usi008':
    case 'sectionIntro':
    case 'focFeedback':
      host.closeMoreMenu?.();
      host.collapseFirstIncident?.();
      return;
    default:
      host.closeMoreMenu?.();
      host.collapseFirstIncident?.();
  }
}

/** Navigate helper used by hosts that own routing. */
export function focIncidentsWizardHref(): string {
  return FOC_INCIDENTS_TOUR_WIZARD_HREF;
}

export function resetFocIncidentsTourBridgeForTests() {
  api = {};
  pendingStep = null;
}
